import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { SbkLoansDataService } from '../sbk-loans.service';
import { applyCarLoan, applyCarLoanFailure, applyCarLoanSuccess, applyCreditCardLoan, applyCreditCardLoanFailure, applyCreditCardLoanSuccess, applyHomeLoan, applyHomeLoanFailure, applyHomeLoanSuccess, applyLoan, applyLoanFailure, applyLoanSuccess, getActiveLoans, getActiveLoansFailure, getActiveLoansSuccess, getAvailableLoans, getAvailableLoansFailure, getAvailableLoansSuccess, getLoanPurposes, getLoanPurposesFailure, getLoanPurposesSuccess, repayLoan, repayLoanFailure, repayLoanSuccess, topUpLoan, topUpLoanFailure, topUpLoanSuccess } from './actions';
import { CustomNotificationService, setLoading } from '@sbk/shared';
import { Store } from '@ngrx/store';

@Injectable()
export class LoansEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);

  /**
   * Get available Loans
  */
  getAvailableLoans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAvailableLoans),
      tap(() => this.store.dispatch(setLoading({ key: 'getAvailableLoans', loading: true }))),
      mergeMap((action) =>
        this.dataService.getAvailableLoans(action.loanType).pipe(
          map((response) => getAvailableLoansSuccess(
            {
              availableLoans: response
            }
          )),
          catchError((error) => of(getAvailableLoansFailure({ error: error?.statusText }))),
          finalize(() => this.store.dispatch(setLoading({ key: 'getAvailableLoans', loading: false }))),
        )
      )
    )
  );

  /**
   * Get active Loans
  */
  getActiveLoans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getActiveLoans),
      tap(() => this.store.dispatch(setLoading({ key: 'getActiveLoans', loading: true }))),
      switchMap(() =>
        this.dataService.getActiveLoans().pipe(
          map((response) => getActiveLoansSuccess(
            {
              activeLoans: response
            }
          )),
          catchError((error) => of(getActiveLoansFailure({ error: error?.statusText }))),
          finalize(() => this.store.dispatch(setLoading({ key: 'getActiveLoans', loading: false }))),
        )
      )
    )
  );

  getActiveLoansFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(getActiveLoansFailure),
        tap((action) => {
          this.customNotificationService.showNotification(
            'Failed to Fetch Loans', `${action.error}`, 'error');
        }),
      );
    },
    { dispatch: false }
  );

  /**
   * Apply for a loan
  */
  applyLoan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(applyLoan),
      mergeMap((action) => {
        this.store.dispatch(setLoading({ key: 'applyLoan', loading: true }));

        return this.dataService.applyLoan(action.payload).pipe(
          mergeMap((response: any) => {
            const confirmationId = response?.confirmationId;

            // Resend application request post transaction signing together with the confirmationID
            return this.dataService.confirmLoanApplicationStatus(action.payload, confirmationId).pipe(
              map((response) => {
                const loanDetails = {
                  ...action.payload,
                  confirmationId,
                };
                if (response?.loanStatus === 'SUCCESS') {
                  return applyLoanSuccess({ loanDetails });
                } else {
                  return applyLoanFailure({ error: response?.responseMessage })
                }
              }),
              catchError((error) => of(applyLoanFailure({ error: error?.statusText })))
            );
          }),
          catchError((error) => of(applyLoanFailure({ error: error?.statusText }))),
          finalize(() => {
            this.store.dispatch(setLoading({ key: 'applyLoan', loading: false }));
          })
        );
      })
    );
  });

  applyLoanSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(applyLoanSuccess),
        tap(() => {
          this.customNotificationService.showNotification(
            'Loan Application Submitted', `Your loan application was submitted successfully`, 'success');
        }),
      );
    },
    { dispatch: false }
  );

  applyLoanFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(applyLoanFailure),
        tap((action) => {
          this.customNotificationService.showNotification(
            'Application Failed', `${action.error}`, 'error');
        }),
      );
    },
    { dispatch: false }
  );


  /**
 * Apply for a loan
*/
  applyCarLoan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(applyCarLoan),
      tap(() => this.store.dispatch(setLoading({ key: 'applyCarLoan', loading: true }))),
      switchMap((action) => {
        return this.dataService.applyCarLoan(action.payload).pipe(
          map((response) => {
            this.store.dispatch(setLoading({ key: 'applyCarLoan', loading: false }));
            const loanDetails = response;

            if (response?.ResponseCode === "200") {
              return applyCarLoanSuccess({ loanDetails });
            } else {
              return applyCarLoanFailure({ error: response?.responseMessage });
            }
          }),
          catchError((error) => {
            this.store.dispatch(setLoading({ key: 'applyCarLoan', loading: false }));
            return of(applyCarLoanFailure({ error: error?.statusText }));
          })
        );
      })
    );
  });

  applyCarLoanSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(applyCarLoanSuccess),
        tap(() => {
          this.customNotificationService.showNotification(
            'Car Loan Request Submitted', `A specialist will contact you within 24 hours`, 'success');
        }),
      );
    },
    { dispatch: false }
  );

  applyCarLoanFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(applyCarLoanFailure),
        tap((action) => {
          this.customNotificationService.showNotification(
            'Application Failed', `${action.error}`, 'error');
        }),
      );
    },
    { dispatch: false }
  );

  /**
 * Apply for a home loan
*/
  applyHomeLoan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(applyHomeLoan),
      tap(() => this.store.dispatch(setLoading({ key: 'applyHomeLoan', loading: true }))),
      switchMap((action) => {
        return this.dataService.applyHomeLoan(action.payload).pipe(
          map((response) => {
            this.store.dispatch(setLoading({ key: 'applyHomeLoan', loading: false }));
            const loanDetails = response;

            if (response?.ResponseCode === "200") {
              return applyHomeLoanSuccess({ loanDetails });
            } else {
              return applyHomeLoanFailure({ error: response?.responseMessage });
            }
          }),
          catchError((error) => {
            this.store.dispatch(setLoading({ key: 'applyHomeLoan', loading: false }));
            return of(applyHomeLoanFailure({ error: error?.statusText }));
          })
        );
      })
    );
  });

  applyHomeLoanSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(applyHomeLoanSuccess),
        tap(() => {
          this.customNotificationService.showNotification(
            'Home Loan Request Submitted', `A specialist will contact you within 24 hours`, 'success');
        }),
      );
    },
    { dispatch: false }
  );

  applyHomeLoanFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(applyHomeLoanFailure),
        tap((action) => {
          this.customNotificationService.showNotification(
            'Application Failed', `${action.error}`, 'error');
        }),
      );
    },
    { dispatch: false }
  );

  applyCreditCardLoan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(applyCreditCardLoan),
      tap(() => this.store.dispatch(setLoading({ key: 'applyCreditCardLoan', loading: true }))),
      switchMap((action) => {
        return this.dataService.applyCreditCardLoan(action.payload).pipe(
          map((response) => {
            this.store.dispatch(setLoading({ key: 'applyCreditCardLoan', loading: false }));
            const loanDetails = response;

            if (response?.ResponseCode === '200') {
              return applyCreditCardLoanSuccess({ loanDetails });
            } else {
              return applyCreditCardLoanFailure({ error: response?.responseMessage });
            }
          }),
          catchError((error) => {
            this.store.dispatch(setLoading({ key: 'applyCreditCardLoan', loading: false }));
            return of(applyCreditCardLoanFailure({ error: error?.statusText }));
          })
        );
      })
    );
  });

  applyCreditCardLoanSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(applyCreditCardLoanSuccess),
        tap(() => {
          this.customNotificationService.showNotification(
            'Credit Card Request Submitted', `A specialist will contact you within 24 hours`, 'success');
        }),
      );
    },
    { dispatch: false }
  );

  applyCreditCardLoanFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(applyCreditCardLoanFailure),
        tap((action) => {
          this.customNotificationService.showNotification(
            'Application Failed', `${action.error}`, 'error');
        }),
      );
    },
    { dispatch: false }
  );

  /**
   * Loan top up
  */
  topUpLoan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(topUpLoan),
      mergeMap((action) => {
        this.store.dispatch(setLoading({ key: 'topUpLoan', loading: true }));

        return this.dataService.topUpLoan(action.payload).pipe(
          mergeMap((response: any) => {
            const confirmationId = response?.confirmationId;

            // Resend top-up request post transaction signing together with the confirmationID
            return this.dataService.confirmLoanTopUpStatus(action.payload, confirmationId).pipe(
              map((response) => {
                const loanDetails = {
                  ...action.payload,
                  confirmationId,
                };
                if (response?.loanStatus === 'SUCCESS') {
                  return topUpLoanSuccess({ loanDetails });
                } else {
                  return topUpLoanFailure({ error: response?.responseMessage })
                }
              }),
              catchError((error) => of(topUpLoanFailure({ error: error?.statusText })))
            );
          }),
          catchError((error) => of(topUpLoanFailure({ error: error?.statusText }))),
          finalize(() => {
            this.store.dispatch(setLoading({ key: 'topUpLoan', loading: false }));
          })
        );
      })
    );
  });

  topUpLoanSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(topUpLoanSuccess),
        tap(() => {
          this.customNotificationService.showNotification(
            'Loan Top Up Submitted', `Your loan top-up request was submitted successfully.`, 'success');
        }),
      );
    },
    { dispatch: false }
  );

  topUpLoanFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(topUpLoanFailure),
        tap((action) => {
          this.customNotificationService.showNotification(
            'Loan Top Up Failed', `${action.error}`, 'error'
          );
        }),
      );
    },
    { dispatch: false }
  );

  /**
   * Repay loan
  */
  repayLoan$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(repayLoan),
      mergeMap((action) => {
        this.store.dispatch(setLoading({ key: 'repayLoan', loading: true }));

        return this.dataService.repayLoan(action.payload).pipe(
          mergeMap((response: any) => {
            const confirmationId = response?.confirmationId;

            // Resend repayment request post transaction signing together with the confirmationID
            return this.dataService.confirmLoanRepaymentStatus(action.payload, confirmationId).pipe(
              map((response) => {
                const loanDetails = {
                  ...action.payload,
                  confirmationId,
                };
                if (response?.loanStatus === 'SUCCESS') {
                  return repayLoanSuccess({ loanDetails });
                } else {
                  return repayLoanFailure({ error: response?.responseMessage })
                }
              }),
              catchError((error) => of(repayLoanFailure({ error: error?.statusText })))
            );
          }),
          catchError((error) => of(repayLoanFailure({ error: error?.statusText }))),
          finalize(() => {
            this.store.dispatch(setLoading({ key: 'repayLoan', loading: false }));
          })
        );
      })
    );
  });

  repayLoanSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(repayLoanSuccess),
        tap(() => {
          this.customNotificationService.showNotification(
            'Loan Paid', `Your loan repayment was submitted successfully`, 'success');
        }),
      );
    },
    { dispatch: false }
  );

  repayLoanFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(repayLoanFailure),
        tap((action) => {
          this.customNotificationService.showNotification(
            'Payment failed', `${action.error}`, 'error'
          );
        }),
      );
    },
    { dispatch: false }
  );

  getLoanPurposes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getLoanPurposes),
      tap(() => this.store.dispatch(setLoading({ key: 'getLoanPurposes', loading: true }))),
      switchMap((action) => {
        const request$ = action.loanType === 'home'
          ? this.dataService.getHomeLoanPurposes()
          : action.loanType === 'credit-card'
            ? this.dataService.getCreditCardTypes()
            : this.dataService.getCarLoanPurposes();

        return request$.pipe(
          map((response) => getLoanPurposesSuccess({
            loanType: action.loanType,
            purposes: Array.isArray(response) ? response : []
          })),
          catchError((error) => of(getLoanPurposesFailure({
            loanType: action.loanType,
            error: error?.statusText || 'Failed to fetch loan purposes'
          }))),
          finalize(() => this.store.dispatch(setLoading({ key: 'getLoanPurposes', loading: false })))
        );
      })
    )
  );

  constructor(
    private dataService: SbkLoansDataService,
    private customNotificationService: CustomNotificationService
  ) { }
}
